import { DashboardState, IDisplayItem, SpecialDIIds } from "@kuika/kuika-cl-model";

/**
 * Whether the opaque empty-canvas overlay (see empty-canvas.scss) should be mounted.
 *
 * The overlay is a designer-side layer drawn OVER the agent iframe, so it may only appear
 * when that iframe is genuinely blank. Two things can make it non-blank on a screen whose
 * own root has no children:
 *
 * - layout section DIs (Header/Footer/PageHeader/PageFooter), which are siblings of rootDI
 *   rather than its children, so looking at rootDI alone would hide sections the canvas is
 *   already rendering;
 * - a bound MASTER screen, whose shell the canvas renders around the empty content area
 *   (design-side-element-generator: `hasLayout && masterScreenDesign` →
 *   getMasterScreenLayout(context, getScreenLayout)). KOA-18412: a screen created from a
 *   master's "Add Screen" button is correctly bound, but the overlay covered the master
 *   layout until the first drop turned the screen non-empty — which read as "the new screen
 *   is not attached to its parent".
 *
 * Kept as a pure function, separate from the Canvas component, so the rule can be tested
 * without mounting the canvas (iframe, rulers and scroll wiring).
 */
export interface IEmptyCanvasOverlayInput {
  /** The overlay is a design-mode affordance only. */
  dashboardState: DashboardState | undefined;
  /** The OPEN screen's own display items. */
  displayItems: IDisplayItem[] | undefined;
  /**
   * Id of the master screen design the canvas is actually rendering — i.e. what
   * frame-provider passes to the iframe as `masterScreenDesign`, NOT the screen's
   * configured masterId. When the binding resolves but the master design failed to load
   * the iframe really is blank, and the empty state must still show.
   */
  masterScreenDesignId: string | undefined;
}

export function isScreenTreeEmpty(displayItems: IDisplayItem[] | undefined): boolean {
  const rootDI: IDisplayItem | undefined = displayItems?.find((di) => di.id === SpecialDIIds.rootDI);
  if (!rootDI || !displayItems) {
    return false;
  }
  const hasRootChildren: boolean = (rootDI.children?.length ?? 0) > 0;
  const hasSectionDI: boolean = displayItems.some((di) => di.id !== SpecialDIIds.rootDI);
  return !hasRootChildren && !hasSectionDI;
}

export function isEmptyCanvasOverlayVisible(input: IEmptyCanvasOverlayInput): boolean {
  if (input.dashboardState !== DashboardState.design) {
    return false;
  }
  if (input.masterScreenDesignId !== undefined && input.masterScreenDesignId.length > 0) {
    return false;
  }
  return isScreenTreeEmpty(input.displayItems);
}
